import { useMemo, type JSX } from 'react';
import { NewBlenderFillingEvent } from '../components/BlenderLogbook/BlenderLogbook';
import { useCompressorQuery } from '../lib/queries/compressorQuery';
import { useGasesQuery } from '../lib/queries/gasQuery';
import { useStorageCylinderQuery } from '../lib/queries/storageCylinderQuery';
import { getUserIdFromAccessToken } from '../lib/utils';
import { useDivingCylinderQuery } from '../lib/queries/divingCylinderQuery';
import { useClubCylinderQuery } from '../lib/queries/clubCylinderQuery';

export const BlenderLogbook = (): JSX.Element => {
  const userId = useMemo(() => getUserIdFromAccessToken(), []);

  const { data: allCompressors, isError: isCompressorError } =
    useCompressorQuery();
  const { data: storageCylinders, isError: isStorageCylinderError } =
    useStorageCylinderQuery();
  const { data: gases, isError: isGasesError } = useGasesQuery();
  const { data: divingCylinderSets, isError: isDivingCylindersError } =
    useDivingCylinderQuery(userId);
  const { data: clubCylinderSets } = useClubCylinderQuery();

  const compressors = useMemo(
    () => allCompressors?.filter((c) => !c.airOnly) ?? [],
    [allCompressors],
  );

  const hasFillableCylinderSets =
    (divingCylinderSets?.length ?? 0) > 0 ||
    (clubCylinderSets?.length ?? 0) > 0;

  const requiredDataLoaded = useMemo(
    () =>
      (storageCylinders &&
        storageCylinders.length > 0 &&
        gases &&
        gases.length > 0 &&
        hasFillableCylinderSets) ??
      false,
    [gases, hasFillableCylinderSets, storageCylinders],
  );

  const anyErrors = useMemo(
    () =>
      isCompressorError ||
      isStorageCylinderError ||
      isGasesError ||
      isDivingCylindersError,
    [
      isCompressorError,
      isStorageCylinderError,
      isGasesError,
      isDivingCylindersError,
    ],
  );

  return (
    <>
      {anyErrors && (
        <div>
          Vaadittujen tietojen lataaminen epäonnistui. Yritä ladata sivu
          uudestaan.
        </div>
      )}
      {!hasFillableCylinderSets && (
        <div>
          Sinulla ei ole järjestelmään merkittyä omaa pullosettiä, jota voisi
          täyttää.
        </div>
      )}
      {requiredDataLoaded && (
        <NewBlenderFillingEvent
          compressors={compressors ?? []}
          divingCylinderSets={divingCylinderSets ?? []}
          clubCylinderSets={clubCylinderSets ?? []}
          gases={gases ?? []}
          storageCylinders={storageCylinders ?? []}
        />
      )}
    </>
  );
};
